import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import BrandLogo from "../brand/BrandLogo";
import defaultAvatar from "../../assets/images/avatar.jpg";
import { Collapse } from "react-collapse";
import { useAuth } from "../../contexts/AuthContext";
import { profileAPI } from "../../services/api";
import "./Header.scss";

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const [toggle, setToggle] = useState({
    key: "",
    status: false,
  });
  const [isShowMobile, setShowMobile] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [profileAvatar, setProfileAvatar] = useState(null);
  const userDropdownTimeoutRef = useRef(null);

  useEffect(() => {
    // Ensure mobile menu is closed on mount/remount
    const getMobile = document.querySelector(".menu-mobile-popup");
    if (getMobile) {
      getMobile.classList.remove("modal-menu--open");
    }
    setShowMobile(false);
  }, []);

  // Fetch profile avatar when user is authenticated
  useEffect(() => {
    const fetchProfileAvatar = async () => {
      if (isAuthenticated) {
        try {
          const response = await profileAPI.getProfile();
          if (response.data.success) {
            setProfileAvatar(response.data.data.profile.profilePhoto);
          }
        } catch (error) {
          console.error("Error fetching profile avatar:", error);
        }
      } else {
        setProfileAvatar(null);
      }
    };

    fetchProfileAvatar();

    // Listen for avatar update events
    const handleAvatarUpdate = () => {
      fetchProfileAvatar();
    };

    window.addEventListener('avatarUpdated', handleAvatarUpdate);
    return () => {
      window.removeEventListener('avatarUpdated', handleAvatarUpdate);
    };
  }, [isAuthenticated]);

  const handleToggle = (key) => {
    if (toggle.key === key) {
      setToggle({
        status: false,
      });
    } else {
      setToggle({
        status: true,
        key,
      });
    }
  };

  const handleMobile = () => {
    const getMobile = document.querySelector(".menu-mobile-popup");
    if (getMobile) {
      setShowMobile(!isShowMobile);
      !isShowMobile
        ? getMobile.classList.add("modal-menu--open")
        : getMobile.classList.remove("modal-menu--open");
    }
  };

  const handleAboutClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // If already on home page, scroll to section
      const section = document.getElementById('what-is-talsolution');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // If on another page, navigate to home and then scroll
      navigate('/');
      setTimeout(() => {
        const section = document.getElementById('what-is-talsolution');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 200);
    }
    handleMobile(); // Close mobile menu if open
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // If already on home page, scroll to section
      const section = document.getElementById('contact-section');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // If on another page, navigate to home and then scroll
      navigate('/');
      setTimeout(() => {
        const section = document.getElementById('contact-section');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 200);
    }
    handleMobile(); // Close mobile menu if open
  };

  return (
    <>
      {/* Mobile Menu */}
      <div className="menu-mobile-popup">
        <div className="modal-menu__backdrop" onClick={handleMobile}></div>
        <div className="widget-filter">
          <div className="mobile-header">
            <div id="logo" className="logo">
              <Link to="/">
                <BrandLogo size={32} />
              </Link>
            </div>
            <Link className="title-button-group" onClick={handleMobile}>
              <i className="icon-close"></i>
            </Link>
          </div>

          <div className="nav-wrap">
            <nav className="main-nav mobile">
              <ul id="menu-primary-menu" className="menu">
                <li className="menu-item">
                  <Link to="/" onClick={handleMobile}>Home</Link>
                </li>
                <li className="menu-item">
                  <Link to="/careers" onClick={handleMobile}>Careers</Link>
                </li>
                <li className="menu-item">
                  <Link to="/" onClick={handleAboutClick}>About Us</Link>
                </li>
                <li className="menu-item">
                  <Link to="/" onClick={handleContactClick}>Contact</Link>
                </li>
                {isAuthenticated ? (
                  <>
                    <li className="menu-item">
                      <Link to="/profile" onClick={handleMobile}>Profile</Link>
                    </li>
                    <li className="menu-item">
                      <Link to="/dashboard" onClick={handleMobile}>Dashboard</Link>
                    </li>
                    <li className="menu-item">
                      <Link to="/applications" onClick={handleMobile}>My Jobs</Link>
                    </li>
                    <li className="menu-item">
                      <Link to="/" onClick={() => { logout(); handleMobile(); }}>Log Out</Link>
                    </li>
                  </>
                ) : (
                  <>
                    <li className="menu-item">
                      <Link to="/login" onClick={handleMobile}>Login</Link>
                    </li>
                    <li className="menu-item">
                      <Link to="/register" onClick={handleMobile}>Register</Link>
                    </li>
                  </>
                )}
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* Desktop Header */}
      <header id="header" className="header header-default ts-header">
        <div className="tf-container ct2">
          <div className="row">
            <div className="col-md-12">
              <div className="sticky-area-wrap">
                <div className="header-ct-left">
                  <div id="logo" className="logo">
                    <Link to="/">
                      <BrandLogo size={32} />
                    </Link>
                  </div>
                </div>
                <div className="header-ct-right">
                  <div className="nav-wrap">
                    <nav id="main-nav" className="main-nav">
                      <ul id="menu-primary-menu" className="menu">
                        <li className={`menu-item ${location.pathname === "/" ? "current-item" : ""}`}>
                          <Link to="/">Home</Link>
                        </li>
                        <li className={`menu-item ${location.pathname.startsWith("/careers") ? "current-item" : ""}`}>
                          <Link to="/careers">Careers</Link>
                        </li>
                        <li className="menu-item">
                          <Link to="/" onClick={(e) => {
                            e.preventDefault();
                            if (location.pathname === '/') {
                              const section = document.getElementById('what-is-talsolution');
                              if (section) {
                                section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }
                            } else {
                              navigate('/');
                              setTimeout(() => {
                                const section = document.getElementById('what-is-talsolution');
                                if (section) {
                                  section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                              }, 200);
                            }
                          }}>About Us</Link>
                        </li>
                        <li className="menu-item">
                          <Link to="/" onClick={(e) => {
                            e.preventDefault();
                            if (location.pathname === '/') {
                              const section = document.getElementById('contact-section');
                              if (section) {
                                section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }
                            } else {
                              navigate('/');
                              setTimeout(() => {
                                const section = document.getElementById('contact-section');
                                if (section) {
                                  section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                              }, 200);
                            }
                          }}>Contact</Link>
                        </li>
                      </ul>
                    </nav>
                  </div>
                  {isAuthenticated ? (
                    <div 
                      className="header-customize-item user-dropdown-wrapper"
                      onMouseEnter={() => {
                        // Clear any pending close timeout
                        if (userDropdownTimeoutRef.current) {
                          clearTimeout(userDropdownTimeoutRef.current);
                          userDropdownTimeoutRef.current = null;
                        }
                        setShowUserDropdown(true);
                      }}
                      onMouseLeave={(e) => {
                        // Check if mouse is moving to a child element (trigger, menu, bridge)
                        const relatedTarget = e.relatedTarget;
                        if (relatedTarget && relatedTarget instanceof Node) {
                          // Check if moving to any part of the dropdown system
                          if (
                            e.currentTarget.contains(relatedTarget) ||
                            relatedTarget.closest('.user-dropdown-trigger') ||
                            relatedTarget.closest('.user-dropdown-menu') ||
                            relatedTarget.closest('.user-dropdown-bridge')
                          ) {
                            return; // Don't close if moving within dropdown system
                          }
                        }
                        // Add small delay to allow smooth movement between elements
                        userDropdownTimeoutRef.current = setTimeout(() => {
                          setShowUserDropdown(false);
                        }, 150);
                      }}
                    >
                      <a 
                        href="#" 
                        className="user-dropdown-trigger" 
                        onClick={(e) => e.preventDefault()}
                        onMouseEnter={() => {
                          // Clear any pending close timeout when entering trigger
                          if (userDropdownTimeoutRef.current) {
                            clearTimeout(userDropdownTimeoutRef.current);
                            userDropdownTimeoutRef.current = null;
                          }
                          setShowUserDropdown(true);
                        }}
                      >
                        <img 
                          src={profileAvatar || defaultAvatar} 
                          alt="Profile" 
                          className="user-avatar"
                        />
                        <span className="user-name-text">
                          {user?.fullName || user?.email?.split('@')[0] || 'User'}
                        </span>
                        <span className={`dropdown-arrow ${showUserDropdown ? 'open' : ''}`}>
                          <svg 
                            width="10" 
                            height="6" 
                            viewBox="0 0 10 6" 
                            fill="none" 
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path 
                              d="M1 1L5 5L9 1" 
                              stroke="currentColor" 
                              strokeWidth="1.5" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      </a>
                      {showUserDropdown && (
                        <div 
                          className={`user-dropdown-menu ${showUserDropdown ? 'show' : ''}`}
                          onMouseEnter={() => {
                            // Clear any pending close timeout
                            if (userDropdownTimeoutRef.current) {
                              clearTimeout(userDropdownTimeoutRef.current);
                              userDropdownTimeoutRef.current = null;
                            }
                            setShowUserDropdown(true);
                          }}
                          onMouseLeave={(e) => {
                            // Check if mouse is moving to trigger or bridge
                            const relatedTarget = e.relatedTarget;
                            if (relatedTarget && relatedTarget instanceof Node && (
                              relatedTarget.closest('.user-dropdown-trigger') ||
                              relatedTarget.closest('.user-dropdown-bridge') ||
                              relatedTarget.closest('.user-dropdown-wrapper')
                            )) {
                              return; // Don't close if moving to trigger/bridge/wrapper
                            }
                            // Add small delay
                            userDropdownTimeoutRef.current = setTimeout(() => {
                              setShowUserDropdown(false);
                            }, 100);
                          }}
                        >
                          <div 
                            className="user-dropdown-bridge"
                            onMouseEnter={() => {
                              // Clear any pending close timeout
                              if (userDropdownTimeoutRef.current) {
                                clearTimeout(userDropdownTimeoutRef.current);
                                userDropdownTimeoutRef.current = null;
                              }
                              setShowUserDropdown(true);
                            }}
                            onMouseLeave={(e) => {
                              // Check if moving to trigger or menu
                              const relatedTarget = e.relatedTarget;
                              if (relatedTarget && relatedTarget instanceof Node && (
                                relatedTarget.closest('.user-dropdown-trigger') ||
                                relatedTarget.closest('.user-dropdown-menu') ||
                                relatedTarget.closest('.user-dropdown-wrapper')
                              )) {
                                return; // Don't close if moving to trigger/menu/wrapper
                              }
                              // Add small delay
                              userDropdownTimeoutRef.current = setTimeout(() => {
                                setShowUserDropdown(false);
                              }, 100);
                            }}
                          />
                          <div className="user-dropdown-arrow" />
                          <div className="user-dropdown-item-wrapper">
                            <Link 
                              to="/profile" 
                              className="user-dropdown-item"
                              onClick={() => setShowUserDropdown(false)}
                            >
                              <svg className="inline-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 512 512">
                                <path fill="currentColor" d="M332.64 64.58C313.18 43.57 286 32 256 32c-30.16 0-57.43 11.5-76.8 32.38-19.58 21.11-29.12 49.8-26.88 80.78C156.76 206.28 203.27 256 256 256s99.16-49.71 103.67-110.82c2.27-30.7-7.33-59.33-27.03-80.6zM432 480H80a31 31 0 0 1-24.2-11.13c-6.5-7.77-9.12-18.38-7.18-29.11C57.06 392.94 83.4 353.61 124.8 326c36.78-24.51 83.37-38 131.2-38s94.42 13.5 131.2 38c41.4 27.6 67.74 66.93 76.18 113.75 1.94 10.73-.68 21.34-7.18 29.11A31 31 0 0 1 432 480z"></path>
                              </svg>
                              <span>Profile</span>
                            </Link>
                          </div>
                          <div className="user-dropdown-item-wrapper">
                            <Link 
                              to="/dashboard" 
                              className="user-dropdown-item"
                              onClick={() => setShowUserDropdown(false)}
                            >
                              <i className="icon-dashboard"></i>
                              <span>Dashboard</span>
                            </Link>
                          </div>
                          <div className="user-dropdown-item-wrapper">
                            <Link 
                              to="/applications" 
                              className="user-dropdown-item"
                              onClick={() => setShowUserDropdown(false)}
                            >
                              <i className="icon-work"></i>
                              <span>My Jobs</span>
                            </Link>
                          </div>
                          {/* Alerts – commented out for now; to be implemented later */}
                          {/* <div className="user-dropdown-item-wrapper">
                            <Link 
                              to="/dashboard" 
                              className="user-dropdown-item"
                              onClick={() => setShowUserDropdown(false)}
                            >
                              <i className="icon-bell"></i>
                              <span>Alerts</span>
                            </Link>
                          </div> */}
                          {/* Settings – commented out for now; to be implemented later */}
                          {/* <div className="user-dropdown-item-wrapper">
                            <Link 
                              to="/dashboard" 
                              className="user-dropdown-item"
                              onClick={() => setShowUserDropdown(false)}
                            >
                              <svg className="inline-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 512 512">
                                <circle fill="currentColor" cx="256" cy="256" r="48"></circle>
                                <path fill="currentColor" d="m470.39 300-.47-.38-31.56-24.75a16.11 16.11 0 0 1-6.1-13.33v-11.56a16 16 0 0 1 6.11-13.22L469.92 212l.47-.38a26.68 26.68 0 0 0 5.9-34.06l-42.71-73.9a1.59 1.59 0 0 1-.13-.22A26.86 26.86 0 0 0 401 92.14l-.35.13-37.1 14.93a15.94 15.94 0 0 1-14.47-1.29q-4.92-3.1-10-5.86a15.94 15.94 0 0 1-8.19-11.82l-5.59-39.59-.12-.72A27.22 27.22 0 0 0 298.76 26h-85.52a26.92 26.92 0 0 0-26.45 22.39l-.09.56-5.57 39.67a16 16 0 0 1-8.13 11.82 175.21 175.21 0 0 0-10 5.82 15.92 15.92 0 0 1-14.43 1.27l-37.13-15-.35-.14a26.87 26.87 0 0 0-32.48 11.34l-.13.22-42.77 73.95a26.71 26.71 0 0 0 5.9 34.1l.47.38 31.56 24.75a16.11 16.11 0 0 1 6.1 13.33v11.56a16 16 0 0 1-6.11 13.22L42.08 300l-.47.38a26.68 26.68 0 0 0-5.9 34.06l42.71 73.9a1.59 1.59 0 0 1 .13.22 26.86 26.86 0 0 0 32.45 11.3l.35-.13 37.07-14.93a15.94 15.94 0 0 1 14.47 1.29q4.92 3.11 10 5.86a15.94 15.94 0 0 1 8.19 11.82l5.56 39.59.12.72A27.22 27.22 0 0 0 213.24 486h85.52a26.92 26.92 0 0 0 26.45-22.39l.09-.56 5.57-39.67a16 16 0 0 1 8.18-11.82c3.42-1.84 6.76-3.79 10-5.82a15.92 15.92 0 0 1 14.43-1.27l37.13 14.95.35.14a26.85 26.85 0 0 0 32.48-11.34 2.53 2.53 0 0 1 .13-.22l42.71-73.89a26.7 26.7 0 0 0-5.89-34.11zm-134.48-40.24a80 80 0 1 1-83.66-83.67 80.21 80.21 0 0 1 83.66 83.67z"></path>
                              </svg>
                              <span>Settings</span>
                            </Link>
                          </div> */}
                          <div className="user-dropdown-item-wrapper">
                            <button 
                              className="user-dropdown-item user-dropdown-item-button"
                              onClick={() => {
                                logout();
                                setShowUserDropdown(false);
                              }}
                            >
                              <i className="icon-log-out"></i>
                              <span>Log Out</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="header-customize-item ts-header-auth">
                      <Link to="/login" className="ts-header-login">Log in</Link>
                      <Link to="/register" className="ts-header-cta">Create free profile</Link>
                    </div>
                  )}
                  <div className="nav-filter" onClick={handleMobile}>
                    <div className="nav-mobile">
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}

export default Header;
